import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { faExternalLinkAlt } from '@fortawesome/free-solid-svg-icons/faExternalLinkAlt';
import { faPlus } from '@fortawesome/free-solid-svg-icons/faPlus';
import {
  Button,
  cn,
  Icon,
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
  SheetBody,
  SheetFooter,
} from '@appcues/sonar-builder';
import {
  selectUserProperties,
  userPropertiesShape,
} from 'ext/entities/user-properties';
import {
  create as createLocale,
  remove as removeLocale,
  update as updateLocale,
  Shape as LocalesShape,
  setLocale,
  selectActiveLocale,
  selectLocales,
} from 'ext/entities/locales';

import {
  getLanguageFromCode,
  DEFAULT_LOCALE,
  LOCALIZATION_BANNERS_LINK,
} from 'ext/lib/localization';
import { LocalesList } from './LocalesList';
import { AddLocale } from './AddLocale';

export const LocalesFlyout = ({
  locales = [],
  userProperties,
  status,
  open,
  portal,
  onClose,
  overrideSelect,
  onCreateLocale,
  onRemoveLocale,
  onUpdateLocale,
  onSetLocale,
  activeLocale,
  learnMoreLink = LOCALIZATION_BANNERS_LINK,
  elementRef, // Optional: EventBus ref to indicate when dialog should render
}) => {
  const [isAddingLanguage, setIsAddingLanguage] = useState(false);
  const [editingLanguage, setEditingLanguage] = useState();

  // Support both EventBus mode and legacy mode for backward compatibility:
  // - EventBus mode: elementRef is provided, render when it has a truthy value
  // - Legacy mode: no elementRef, render based on `open` prop
  // This allows consumers that haven't migrated to EventBus (like NPS/Launchpad)
  // to continue working alongside EventBus consumers (like Banner).
  const shouldRender = elementRef !== undefined || open;

  const handleFlyoutClose = () => {
    setIsAddingLanguage(false);
    setEditingLanguage();
    onClose();
  };

  const localesList = locales.map(({ id, name, conditions }) => {
    const { property, value } = conditions?.and[0].properties ?? {};

    const propertyName = userProperties?.find(
      userProperty => userProperty.value === property
    )?.name;

    const targetCriteriaNames =
      value
        ?.split('\n')
        ?.map(targetCriteriaCode => getLanguageFromCode(targetCriteriaCode)) ??
      [];

    return {
      id,
      name,
      property: propertyName,
      targetCriteria: targetCriteriaNames,
    };
  });

  const handleRemoveLocale = id => {
    onRemoveLocale(id);
    // Only reset to default locale if the deleted locale is the currently active one
    if (activeLocale?.id === id) {
      onSetLocale(DEFAULT_LOCALE.id);
    }
  };

  // Don't render if not open/shown
  if (!shouldRender) return null;

  // In EventBus mode (elementRef provided), always render as open and with overrideSelect true
  // In legacy mode (no elementRef), use the open prop and passed overrideSelect value
  const isOpen = elementRef !== undefined ? true : open;
  const shouldOverrideSelect = elementRef !== undefined ? true : overrideSelect;

  return (
    <Sheet
      open={isOpen}
      modal={false}
      showOverlay={false}
      onOpenChange={nextOpen => {
        if (!nextOpen) handleFlyoutClose();
      }}
    >
      <SheetContent
        container={portal}
        className={cn('w-[560px] max-w-[90vw] sm:max-w-[560px]')}
      >
        {isAddingLanguage || editingLanguage ? (
          <AddLocale
            userProperties={userProperties}
            setIsAddingLanguage={setIsAddingLanguage}
            isEditingLanguage={editingLanguage}
            setEditingLanguage={setEditingLanguage}
            onCreateLocale={onCreateLocale}
            onUpdateLocale={onUpdateLocale}
            locales={localesList}
            overrideSelect={shouldOverrideSelect}
          />
        ) : (
          <>
            <SheetHeader>
              <SheetTitle>Manage Languages</SheetTitle>
              <SheetDescription className={cn('px-0 pb-0')}>
                Tell us which languages you plan to support, as well as the
                criteria we should use when targeting those speakers.
              </SheetDescription>
            </SheetHeader>
            <SheetBody className={cn('px-6 py-2')}>
              <LocalesList
                portal={portal}
                locales={localesList}
                status={status}
                setEditingLanguage={setEditingLanguage}
                onRemoveLocale={handleRemoveLocale}
              />
            </SheetBody>
            <SheetFooter className={cn('grid grid-cols-2 gap-2')}>
              <Button
                onClick={() => window.open(learnMoreLink, '_blank')}
                variant="ghost"
                fullWidth
              >
                Learn more
                <Icon icon={faExternalLinkAlt} />
              </Button>
              <Button
                onClick={() => setIsAddingLanguage(true)}
                variant="primary"
                fullWidth
              >
                <Icon icon={faPlus} />
                Add language
              </Button>
            </SheetFooter>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
};

LocalesFlyout.propTypes = {
  learnMoreLink: PropTypes.string,
  locales: PropTypes.arrayOf(LocalesShape),
  userProperties: userPropertiesShape,
  status: PropTypes.string,
  open: PropTypes.bool,
  portal: PropTypes.oneOfType([PropTypes.element, PropTypes.object]),
  onClose: PropTypes.func,
  onCreateLocale: PropTypes.func,
  onRemoveLocale: PropTypes.func,
  onUpdateLocale: PropTypes.func,
  overrideSelect: PropTypes.bool,
  onSetLocale: PropTypes.func,
  activeLocale: LocalesShape,
  elementRef: PropTypes.shape({
    current: PropTypes.instanceOf(Element),
  }),
};

const mapStateToProps = (state, ownProps) => ({
  userProperties: selectUserProperties(state),
  activeLocale: selectActiveLocale(state),
  // Use locales from props if provided, otherwise get from Redux state
  locales: ownProps.locales ?? Object.values(selectLocales(state) ?? {}),
});

const mapDispatchToProps = {
  onCreateLocale: createLocale,
  onRemoveLocale: removeLocale,
  onUpdateLocale: updateLocale,
  onSetLocale: setLocale,
};

export default connect(mapStateToProps, mapDispatchToProps)(LocalesFlyout);
